import React, { useState, useEffect, useRef } from "react";
import './PagesCss/Contact.css';

/* ── reason options ── */
const REASONS = [
  { value: "",                label: "Select a reason…" },
  { value: "general",         label: "General Enquiry" },
  { value: "collaboration",   label: "Collaboration / Partnership" },
  { value: "product-idea",    label: "Product Idea or Feedback" },
  { value: "bug",             label: "Bug Report" },
  { value: "press",           label: "Press / Media" },
  { value: "other",           label: "Something Else" },
];

/* ── contact info cards ── */
const INFO_CARDS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ct-info-svg">
        <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M3 8l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: "Email",
    value: "business@d-techstudios.com",
    sub: "We reply within 24–48 hours",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ct-info-svg">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/>
        <path d="M12 3C12 3 8 7 8 12C8 17 12 21 12 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M12 3C12 3 16 7 16 12C16 17 12 21 12 21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M3 12H21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
    ),
    label: "Studio",
    value: "d-techstudios.com",
    sub: "Independent AI & SaaS Studio",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ct-info-svg">
  <path d="M12 3L4 7v5c0 4.55 3.4 8.74 8 9.93C16.6 20.74 20 16.55 20 12V7l-8-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
    ),
    label: "D-U-N-S",
    value: "137584078",
    sub: "Verified",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ct-info-svg">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="1.6"/>
        <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
    label: "Location",
    value: "2568 Madison AVE",
    sub: "League City, TX 77573",
  },
];

/* ── intersection reveal hook ── */
function useReveal() {
  const [seen, setSeen] = useState(new Set());
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setSeen((p) => new Set([...p, e.target.dataset.key]));
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll("[data-key]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (k) => seen.has(k);
}

/* ── Terminal ping animation ── */
function PingTerminal() {
  const [lines, setLines] = useState([]);
  const bodyRef = useRef(null);

  const LOGS = [
    { type: "system", text: "D-Tech Studios · Contact gateway online",  delay: 0 },
    { type: "task",   text: "Listening on contact@d-techstudios.com",   delay: 700 },
    { type: "ok",     text: "Avg. response time: < 48 hrs",             delay: 1500 },
    { type: "info",   text: "All messages are read personally",         delay: 2400 },
    { type: "ok",     text: "Collaboration enquiries: welcome",         delay: 3300 },
    { type: "system", text: "Ready to receive  ●",                      delay: 4200 },
  ];

  useEffect(() => {
    const timers = LOGS.map((l, i) =>
      setTimeout(() => setLines((p) => [...p, l]), l.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines]);

  const prefix = { system: "◈ SYS", task: "▶ RUN", ok: "✓ OUT", info: "ℹ INF" };

  return (
    <div className="ct-terminal">
      <div className="ct-term-bar">
        <div className="ct-term-dots">
          <span className="ct-td ct-td-r" />
          <span className="ct-td ct-td-y" />
          <span className="ct-td ct-td-g" />
        </div>
        <span className="ct-term-title">contact-gateway — bash</span>
        <span className="ct-term-badge">● LIVE</span>
      </div>
      <div className="ct-term-path">
        <span className="ct-tp-dir">~/d-tech/contact</span>
        <span className="ct-tp-branch"> git:(main)</span>
      </div>
      <div className="ct-term-body" ref={bodyRef}>
        {lines.map((l, i) => (
          <div className={`ct-tl ct-tl-${l.type}`} key={i}>
            <span className="ct-tl-pre">{prefix[l.type]}</span>
            <span className="ct-tl-txt">{l.text}</span>
          </div>
        ))}
      </div>
      <div className="ct-term-foot">
        <span className="ct-tf-item">gateway: active</span>
        <span className="ct-tf-sep">·</span>
        <span className="ct-tf-item ct-tf-ok">all systems nominal</span>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   PAGE COMPONENT
═══════════════════════════════════════════════════ */
export default function Contact() {
  const vis = useReveal();

  const [form, setForm] = useState({
    name: "", email: "", reason: "", message: "",
  });
  const [errors, setErrors]   = useState({});
  const [status, setStatus]   = useState("idle"); // idle | sending | success | error
  const [focused, setFocused] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                           e.name    = "Name is required.";
    if (!form.email.trim())                          e.email   = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.reason)                                e.reason  = "Please select a reason.";
    if (!form.message.trim())                        e.message = "Message cannot be empty.";
    else if (form.message.trim().length < 20)        e.message = "Please write at least 20 characters.";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus("sending");
    // Simulate async send
    setTimeout(() => {
      setStatus("success");
      setForm({ name: "", email: "", reason: "", message: "" });
    }, 1800);
  };

  return (
    <div className="ct-root">

      {/* ══ HERO ══ */}
      <section className="ct-hero">
        <div className="ct-hero-l">
          <p className="ct-eyebrow">
            <span className="ct-eyebrow-line" />
            Get In Touch
          </p>
          <h1 className="ct-h1">
            Let's build<br />
            something<br />
            <span className="ct-h1-muted">together.</span>
          </h1>
          <p className="ct-hero-body">
            Have a product idea, a collaboration in mind, or just want to say hello?
            Every message is read personally by the D-Tech Studios team. No bots, no
            auto-replies just real people who care.
          </p>

          {/* info cards */}
          <div className="ct-info-cards">
            {INFO_CARDS.map((c, i) => (
              <div className="ct-info-card" key={i}>
                <span className="ct-info-icon">{c.icon}</span>
                <div className="ct-info-text">
                  <span className="ct-info-label">{c.label}</span>
                  <span className="ct-info-value">{c.value}</span>
                  <span className="ct-info-sub">{c.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* right: terminal */}
        <div className="ct-hero-r">
          <PingTerminal />

          {/* floating chips */}
          <div className="ct-hero-chips">
            <div className="ct-chip">
              <span className="ct-chip-val">&lt; 48h</span>
              <span className="ct-chip-lbl">response time</span>
            </div>
            <div className="ct-chip">
              <span className="ct-chip-val">100%</span>
              <span className="ct-chip-lbl">personally read</span>
            </div>
            <div className="ct-chip">
              <span className="ct-chip-val">Open</span>
              <span className="ct-chip-lbl">to collabs</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══ MARQUEE ══ */}
      <div className="ct-marquee">
        <div className="ct-marquee-track">
          {["Get In Touch", "Collaborate", "Build Together", "Share Ideas", "Say Hello", "Partner With Us"].concat(
            ["Get In Touch", "Collaborate", "Build Together", "Share Ideas", "Say Hello", "Partner With Us"]
          ).map((t, i) => (
            <span key={i} className="ct-marquee-item">
              {t}<span className="ct-marquee-sep">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* ══ CONTACT FORM ══ */}
      <section
        className={`ct-section ct-form-section ${vis("form") ? "ct-in" : ""}`}
        data-key="form"
      >
        <div className="ct-form-inner">

          {/* left: copy */}
          <div className="ct-form-l">
            <p className="ct-tag">Send a message</p>
            <h2 className="ct-h2">
              Tell us what's<br />
              <em>on your mind.</em>
            </h2>
            <p className="ct-form-body">
              Whether you're a developer, founder, designer, or just someone with an idea
              we want to hear from you. Fill in the form and we'll get back to you as soon as possible.
            </p>

            <div className="ct-form-promise">
              {[
                "No spam, ever.",
                "We reply personally.",
                "All ideas are confidential.",
              ].map((t, i) => (
                <div className="ct-promise-item" key={i}>
                  <span className="ct-promise-dot" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* right: form */}
          <div className="ct-form-r">
            {status === "success" ? (
              <div className="ct-success">
                <div className="ct-success-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6"/>
                    <path d="M7.5 12l3 3 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="ct-success-h">Message sent!</h3>
                <p className="ct-success-p">
                  Thanks for reaching out. We'll get back to you within 24–48 hours.
                </p>
                <button className="ct-success-btn" onClick={() => setStatus("idle")}>
                  Send another message
                </button>
              </div>
            ) : (
              <form className="ct-form" onSubmit={handleSubmit} noValidate>

                {/* row: name + email */}
                <div className="ct-form-row">
                  <div className={`ct-field ${focused === "name" ? "ct-field--focused" : ""} ${errors.name ? "ct-field--error" : ""}`}>
                    <label className="ct-label" htmlFor="ct-name">Full Name</label>
                    <input
                      id="ct-name"
                      name="name"
                      type="text"
                      className="ct-input"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={handleChange}
                      onFocus={() => setFocused("name")}
                      onBlur={() => setFocused(null)}
                      autoComplete="name"
                    />
                    {errors.name && <span className="ct-error">{errors.name}</span>}
                  </div>

                  <div className={`ct-field ${focused === "email" ? "ct-field--focused" : ""} ${errors.email ? "ct-field--error" : ""}`}>
                    <label className="ct-label" htmlFor="ct-email">Email Address</label>
                    <input
                      id="ct-email"
                      name="email"
                      type="email"
                      className="ct-input"
                      placeholder="jane@example.com"
                      value={form.email}
                      onChange={handleChange}
                      onFocus={() => setFocused("email")}
                      onBlur={() => setFocused(null)}
                      autoComplete="email"
                    />
                    {errors.email && <span className="ct-error">{errors.email}</span>}
                  </div>
                </div>

                {/* reason */}
                <div className={`ct-field ${focused === "reason" ? "ct-field--focused" : ""} ${errors.reason ? "ct-field--error" : ""}`}>
                  <label className="ct-label" htmlFor="ct-reason">Reason for Contact</label>
                  <div className="ct-select-wrap">
                    <select
                      id="ct-reason"
                      name="reason"
                      className="ct-select"
                      value={form.reason}
                      onChange={handleChange}
                      onFocus={() => setFocused("reason")}
                      onBlur={() => setFocused(null)}
                    >
                      {REASONS.map((r) => (
                        <option key={r.value} value={r.value} disabled={r.value === ""}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    <span className="ct-select-arrow">
                      <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" width="12" height="12">
                        <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                  {errors.reason && <span className="ct-error">{errors.reason}</span>}
                </div>

                {/* message */}
                <div className={`ct-field ${focused === "message" ? "ct-field--focused" : ""} ${errors.message ? "ct-field--error" : ""}`}>
                  <label className="ct-label" htmlFor="ct-message">
                    Message
                    <span className="ct-char-count">{form.message.length} / 1000</span>
                  </label>
                  <textarea
                    id="ct-message"
                    name="message"
                    className="ct-textarea"
                    placeholder="Tell us what's on your mind…"
                    rows={6}
                    maxLength={1000}
                    value={form.message}
                    onChange={handleChange}
                    onFocus={() => setFocused("message")}
                    onBlur={() => setFocused(null)}
                  />
                  {errors.message && <span className="ct-error">{errors.message}</span>}
                </div>

                {/* submit */}
                <div className="ct-form-foot">
                  <p className="ct-form-note">
                    By submitting, you agree to our Privacy Policy. Your data is never sold.
                  </p>
                  <button
                    type="submit"
                    className={`ct-submit ${status === "sending" ? "ct-submit--sending" : ""}`}
                    disabled={status === "sending"}
                  >
                    {status === "sending" ? (
                      <>
                        <span className="ct-spinner" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send Message
                        <span className="ct-submit-arr">→</span>
                      </>
                    )}
                  </button>
                </div>

              </form>
            )}
          </div>
        </div>
      </section>

      {/* ══ FAQ STRIP ══ */}
      <section
        className={`ct-section ct-faq ${vis("faq") ? "ct-in" : ""}`}
        data-key="faq"
      >
        <div className="ct-faq-head">
          <p className="ct-tag">Common questions</p>
          <h2 className="ct-h2">Before you write.</h2>
        </div>
        <div className="ct-faq-grid">
          {[
            {
              q: "Do you take on freelance or contract work?",
              a: "Not at this stage. D-Tech Studios is focused on building its own in-house products. However, we're always open to genuine partnerships and collaborations.",
            },
            {
              q: "I have a product idea can I pitch it?",
              a: "Yes, absolutely. We love hearing ideas from the community. Use the contact form and select 'Product Idea or Feedback' as the reason.",
            },
            {
              q: "How long until you reply?",
              a: "We aim to respond to every message within 24–48 hours. During peak periods it may take a little longer, but we will always get back to you.",
            },
            {
              q: "Can I report a bug or request a feature?",
              a: "Please do. Select 'Bug Report' from the dropdown and include as much detail as possible what product, what happened, and what you expected.",
            },
          ].map((item, i) => (
            <FaqItem key={i} q={item.q} a={item.a} index={i} />
          ))}
        </div>
      </section>

    </div>
  );
}

/* ── Accordion FAQ item ── */
function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (bodyRef.current) setHeight(bodyRef.current.scrollHeight);
  }, [a]);

  return (
    <div
      className={`ct-faq-item ${open ? "ct-faq-item--open" : ""}`}
      style={{ animationDelay: `${index * 0.07}s` }}
    >
      <button className="ct-faq-q" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>{q}</span>
        <span className="ct-faq-icon">
          <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
            <line x1="10" y1="4" x2="10" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
              style={{ transform: open ? "scaleY(0)" : "scaleY(1)", transformOrigin: "center", transition: "transform 0.25s" }}
            />
            <line x1="4" y1="10" x2="16" y2="10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      <div
        className="ct-faq-body-wrap"
        style={{
          maxHeight: open ? `${height + 32}px` : "0px",
          overflow: "hidden",
          transition: "max-height 0.38s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <div ref={bodyRef} className="ct-faq-body">
          <p>{a}</p>
        </div>
      </div>
    </div>
  );
}